import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getPaddleEnvironment, TIER_RANK, TIER_LIMITS } from "@/lib/paddle";

export type Subscription = {
  id: string;
  paddle_subscription_id: string;
  product_id: string;
  price_id: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
};

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const refetch = useCallback(async () => {
    if (!user?.id) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .eq("environment", getPaddleEnvironment())
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Failed to fetch subscription:", error);
      setSubscription(null);
      setLoading(false);
      return;
    }

    setSubscription((data as Subscription) ?? null);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      setSubscription(null);
      setLoading(false);
      return;
    }

    void refetch();

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(
        `subscription-${user.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void refetch();
        }
      )
      .subscribe((status, error) => {
        if (error) {
          console.error("Subscription realtime channel error:", error);
        }

        if (status === "CHANNEL_ERROR") {
          console.error("Subscription realtime channel failed");
        }
      });


    channelRef.current = channel;

    return () => {
      if (channelRef.current === channel) {
        channelRef.current = null;
      }

      supabase.removeChannel(channel);
    };
  }, [user?.id, refetch]);

  const now = new Date();
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end)
    : null;

  const isActive =
    !!subscription &&
    ["active", "trialing", "past_due"].includes(subscription.status) &&
    (!periodEnd || periodEnd > now);

  const tier = subscription?.product_id ?? null;
  const tierRank = tier ? TIER_RANK[tier] ?? 0 : 0;
  const limits = tier ? TIER_LIMITS[tier] : null;

  const hasTier = (required: keyof typeof TIER_RANK) =>
    isActive && tierRank >= (TIER_RANK[required] ?? 0);

  return {
    subscription,
    loading,
    isActive,
    tier,
    tierRank,
    limits,
    hasTier,
    refetch,
  };
}
